/**
 * Copyright 2026, CS GROUP, https://www.cs-soprasteria.com/
 *
 * This file is part of AntFlow project.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * @ts-expect-error component non-type it will be type
 */
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck
/* eslint-disable */
import * as L from 'leaflet';
import { LeafletAreaSelect } from '@/ogc-processes/composants/leaflet-areaselect/types.ts';


const AreaSelect = L.Class.extend({
  includes: L.Evented.prototype,

  options: {
    width: 200,
    height: 300,
    minWidth: 30,
    minHeight: 30,
    minHorizontalSpacing: 30,
    minVerticalSpacing: 30,
    keepAspectRatio: false
  },

  initialize: function (options) {
    L.Util.setOptions(this, options);

    this._width = this.options.width;
    this._height = this.options.height;
    this._manualBounds = null;
  },

  addTo: function (map) {
    this.map = map;
    this._createElements();
    this._render();
    return this;
  },

  getBounds: function () {
    if (this._manualBounds) {
      const bounds = this._manualBounds;
      this.clearOffManualBounds();
      return bounds;
    }
    const size = this.map.getSize();
    const topRight = new L.Point();
    const bottomLeft = new L.Point();

    bottomLeft.x = Math.round((size.x - this._width) / 2);
    topRight.y = Math.round((size.y - this._height) / 2);
    topRight.x = size.x - bottomLeft.x;
    bottomLeft.y = size.y - topRight.y;

    const sw = this.map.containerPointToLatLng(bottomLeft);
    const ne = this.map.containerPointToLatLng(topRight);

    return new L.LatLngBounds(sw, ne);
  },

  /**
   * Adjust the map bounds to fit the desired selection, and resize the selection to match the bounds parameter.
   *
   * Note that the actual bounds after running this function may not match the bounds parameter exactly, since
   * we're converting between pixels and latlng coordinates.
   *
   * This method is not compatible with the keepAspectRatio option.
   */
  setBounds: function (bounds, notifyChange = true) {
    bounds = L.latLngBounds(bounds);

    this._manualBounds = bounds;

    if (!notifyChange) {
      this.removeMapListeners();
    }

    this.map.fitBounds(bounds, {
      animate: false,
      paddingTopLeft: [this.options.minHorizontalSpacing / 2, this.options.minVerticalSpacing / 2],
      paddingBottomRight: [this.options.minHorizontalSpacing / 2, this.options.minVerticalSpacing / 2]
    });

    const nwPx = this.map.latLngToLayerPoint(bounds.getNorthWest());
    const sePx = this.map.latLngToLayerPoint(bounds.getSouthEast());
    this.setDimensions(
      {
        width: sePx.x - nwPx.x,
        height: sePx.y - nwPx.y
      },
      notifyChange
    );

    this.handleMapListeners();

  },

  clearOffManualBounds: function () {
    this._manualBounds = null;
    this._isManualChange = false;
  },

  isManualBoundsState: function () {
    return this._isManualChange;
  },

  handleMapListeners: function () {
    this.map.on('moveend', this._onMapChange, this);
    this.map.on('zoomend', this._onMapChange, this);
    this.map.on('resize', this._onMapResize, this);
  },

  removeMapListeners: function () {
    this.map.off('moveend', this._onMapChange, this);
    this.map.off('zoomend', this._onMapChange, this);
    this.map.off('resize', this._onMapResize, this);
  },

  remove: function () {
    this.removeMapListeners();
    this._container.parentNode.removeChild(this._container);
  },

  setDimensions: function (dimensions, notifyChange = true) {
    if (!dimensions) {
      return;
    }

    this._height = parseInt(dimensions.height) || this._height;
    this._width = parseInt(dimensions.width) || this._width;
    this._render();
    if (notifyChange) {
      this.fire('change');
    }
  },

  getDimensions: function () {
    return {
      height: this._height,
      width: this._width
    };
  },

  _createElements: function () {
    if (this._container) {
      return;
    }

    this._container = L.DomUtil.create('div', 'leaflet-areaselect-container', this.map._controlContainer);
    this._topShade = L.DomUtil.create('div', 'leaflet-areaselect-shade leaflet-control', this._container);
    this._bottomShade = L.DomUtil.create('div', 'leaflet-areaselect-shade leaflet-control', this._container);
    this._leftShade = L.DomUtil.create('div', 'leaflet-areaselect-shade leaflet-control', this._container);
    this._rightShade = L.DomUtil.create('div', 'leaflet-areaselect-shade leaflet-control', this._container);

    this._nwHandle = L.DomUtil.create('div', 'leaflet-areaselect-handle leaflet-control', this._container);
    this._swHandle = L.DomUtil.create('div', 'leaflet-areaselect-handle leaflet-control', this._container);
    this._neHandle = L.DomUtil.create('div', 'leaflet-areaselect-handle leaflet-control', this._container);
    this._seHandle = L.DomUtil.create('div', 'leaflet-areaselect-handle leaflet-control', this._container);

    this._setUpHandlerEvents(this._nwHandle);
    this._setUpHandlerEvents(this._neHandle, -1, 1);
    this._setUpHandlerEvents(this._swHandle, 1, -1);
    this._setUpHandlerEvents(this._seHandle, -1, -1);

    this.handleMapListeners();

    this.fire('change');
  },

  _setUpHandlerEvents: function (handle, xMod, yMod) {
    xMod = xMod || 1;
    yMod = yMod || 1;

    const self = this;
    let mapContainer = self.map.getContainer();
    let curX, curY, ratio, size;

    function dragStart(pageX, pageY) {
      self.map.dragging.disable();
      curX = pageX;
      curY = pageY;
      ratio = self._width / self._height;
      size = self.map.getSize();
      mapContainer = self.map.getContainer();
    }
    function dragMove(pageX, pageY) {
      if (self.options.keepAspectRatio) {
        const maxHeight =
          (self._height >= self._width ? size.y : size.x / ratio) -
          Math.max(self.options.minVerticalSpacing, self.options.minHorizontalSpacing);
        self._height += (curY - pageY) * 2 * yMod;
        self._height = Math.max(self.options.minHeight, self.options.minWidth, self._height);
        self._height = Math.min(maxHeight, self._height);
        self._width = self._height * ratio;
      } else {
        self._width += (curX - pageX) * 2 * xMod;
        self._height += (curY - pageY) * 2 * yMod;
        self._width = Math.max(self.options.minWidth, self._width);
        self._height = Math.max(self.options.minHeight, self._height);
        self._width = Math.min(size.x - self.options.minHorizontalSpacing, self._width);
        self._height = Math.min(size.y - self.options.minVerticalSpacing, self._height);
      }

      curX = pageX;
      curY = pageY;
      self._render();
    }
    function dragEnd(event) {
      self.map.dragging.enable();
      self._originalBounds = null;
      self.fire('change');
    }

    // mouse event listeners
    function onMouseMove(event) {
      dragMove(event.pageX, event.pageY);
    }
    function onMouseUp(event) {
      dragEnd(event);
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    }
    L.DomEvent.addListener(handle, 'mousedown', (event) => {
      event.stopPropagation();
      event.preventDefault();

      dragStart(event.pageX, event.pageY);
      document.addEventListener('mousemove', onMouseMove);
      document.addEventListener('mouseup', onMouseUp);
    });

    // touch event listeners
    function onTouchMove(event) {
      dragMove(event.targetTouches[0].pageX, event.targetTouches[0].pageY);
    }
    function onTouchEnd(event) {
      dragEnd(event);
      L.DomEvent.removeListener(mapContainer, 'touchmove', onTouchMove);
      L.DomEvent.removeListener(mapContainer, 'touchend', onTouchEnd);
    }
    L.DomEvent.addListener(handle, 'touchstart', (event) => {
      event.stopPropagation();
      event.preventDefault();

      dragStart(event.targetTouches[0].pageX, event.targetTouches[0].pageY);
      L.DomEvent.addListener(mapContainer, 'touchmove', onTouchMove);
      L.DomEvent.addListener(mapContainer, 'touchend', onTouchEnd);
    });
  },

  _onMapResize: function () {
    this._render();
  },

  _onMapChange: function () {
    this.fire('change');
  },

  _render: function () {
    const size = this.map.getSize();
    const handleOffset = Math.round(this._nwHandle.offsetWidth / 2);

    const topBottomWidth = size.x;
    const topBottomHeight = Math.round((size.y - this._height) / 2);
    const leftRightWidth = Math.round((size.x - this._width) / 2);
    const leftRightHeight = size.y - topBottomHeight * 2;

    function setDimensions(element, dimension) {
      element.style.width = dimension.width + 'px';
      element.style.height = dimension.height + 'px';
      element.style.top = dimension.top + 'px';
      element.style.left = dimension.left + 'px';
      element.style.bottom = dimension.bottom + 'px';
      element.style.right = dimension.right + 'px';
    }

    setDimensions(this._topShade, {
      width: topBottomWidth,
      height: topBottomHeight,
      top: 0,
      left: 0
    });
    setDimensions(this._bottomShade, {
      width: topBottomWidth,
      height: topBottomHeight,
      top: size.y - topBottomHeight,
      left: 0
    });
    setDimensions(this._leftShade, {
      width: leftRightWidth,
      height: leftRightHeight,
      top: topBottomHeight,
      left: 0
    });
    setDimensions(this._rightShade, {
      width: leftRightWidth,
      height: leftRightHeight,
      top: topBottomHeight,
      left: size.x - leftRightWidth
    });

    setDimensions(this._nwHandle, { left: leftRightWidth - handleOffset, top: topBottomHeight - 7 });
    setDimensions(this._neHandle, { right: leftRightWidth - handleOffset, top: topBottomHeight - 7 });
    setDimensions(this._swHandle, { left: leftRightWidth - handleOffset, bottom: topBottomHeight - 7 });
    setDimensions(this._seHandle, { right: leftRightWidth - handleOffset, bottom: topBottomHeight - 7 });
  }
});

function areaSelect(options) {
  return new AreaSelect(options);
}

export { areaSelect };
